import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Settings, 
  Palette, 
  Bell, 
  Shield, 
  Database, 
  User, 
  Sun,
  Moon,
  RefreshCw,
  Mail,
  Download,
  Trash2,
  ChevronRight,
  Info,
  Target,
  TrendingUp,
  Smartphone,
  Monitor,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';

export const SettingsHelpPage: React.FC = () => {
  const navigate = useNavigate();

  const features = [
    {
      id: 'appearance',
      title: 'Aspetto e Tema',
      description: 'Personalizza l\'aspetto dell\'applicazione secondo le tue preferenze.',
      details: [
        '**Tema Chiaro**: Interfaccia luminosa per ambienti ben illuminati',
        '**Tema Scuro**: Interfaccia scura per ridurre l\'affaticamento degli occhi',
        '**Tema Auto**: Si adatta automaticamente alle impostazioni del sistema',
        '**Animazioni**: Attiva/disattiva le animazioni e transizioni',
        '**Riduci movimento**: Disabilita animazioni per accessibilità'
      ],
      icon: <Palette className="h-5 w-5" />
    },
    {
      id: 'notifications',
      title: 'Gestione Notifiche',
      description: 'Configura come ricevere aggiornamenti e promemoria.',
      details: [
        '**Notifiche Push**: Ricevi notifiche in tempo reale sul browser',
        '**Stato Browser**: Verifica supporto e permessi per le notifiche',
        '**Reminder Colloqui**: Promemoria automatici per i colloqui programmati',
        '**Notifiche Email**: Email automatiche per reminder candidature',
        '**Test Notifiche**: Invia notifiche di test per verificare il funzionamento'
      ],
      icon: <Bell className="h-5 w-5" />
    },
    {
      id: 'privacy',
      title: 'Privacy e Sicurezza',
      description: 'Gestisci le impostazioni di privacy e sicurezza del tuo account.',
      details: [
        '**Visibilità Profilo**: Controlla chi può vedere il tuo profilo',
        '**Condivisione Dati**: Gestisci la condivisione dei tuoi dati',
        '**Analytics**: Attiva/disattiva la raccolta dati per miglioramenti',
        '**Cookie**: Gestisci l\'utilizzo dei cookie',
        '**Sicurezza Account**: Impostazioni di sicurezza avanzate'
      ],
      icon: <Shield className="h-5 w-5" />
    },
    {
      id: 'data',
      title: 'Gestione Dati',
      description: 'Esporta, importa e gestisci i tuoi dati personali.',
      details: [
        '**Esporta Dati**: Scarica tutti i tuoi dati in formato JSON',
        '**Backup Automatico**: Backup periodici dei tuoi dati',
        '**Cancellazione Dati**: Rimuovi definitivamente i tuoi dati',
        '**Portabilità**: Trasferisci i tuoi dati ad altri servizi',
        '**Retention**: Gestisci la conservazione dei dati'
      ],
      icon: <Database className="h-5 w-5" />
    },
    {
      id: 'account',
      title: 'Informazioni Account',
      description: 'Gestisci le informazioni del tuo account e preferenze personali.',
      details: [
        '**Profilo Utente**: Modifica nome, email e informazioni personali',
        '**Preferenze**: Impostazioni personalizzate per l\'uso dell\'app',
        '**Ruolo**: Visualizza il tuo ruolo nel sistema',
        '**Stato Account**: Verifica lo stato del tuo account',
        '**Cronologia**: Visualizza la cronologia delle attività'
      ],
      icon: <User className="h-5 w-5" />
    },
    {
      id: 'system',
      title: 'Impostazioni Sistema',
      description: 'Configurazioni avanzate del sistema (solo per admin).',
      details: [
        '**Configurazione Email**: Impostazioni per l\'invio di email',
        '**API Keys**: Gestione delle chiavi API per servizi esterni',
        '**Log Sistema**: Visualizzazione dei log di sistema',
        '**Manutenzione**: Modalità manutenzione e aggiornamenti',
        '**Debug**: Strumenti di debug per sviluppatori'
      ],
      icon: <Settings className="h-5 w-5" />
    }
  ];

  const tips = [
    'Il tema scuro riduce l\'affaticamento degli occhi in ambienti poco illuminati',
    'Le notifiche push richiedono il permesso del browser per funzionare',
    'Puoi esportare i tuoi dati in qualsiasi momento per backup',
    'Le impostazioni vengono salvate automaticamente',
    'Admin e moderatori hanno accesso a funzioni aggiuntive',
    'Usa il pulsante "Test" per verificare le notifiche',
    'Le email di reminder vengono inviate automaticamente dopo 7 giorni',
    'Puoi disattivare le animazioni per migliorare le prestazioni'
  ];

  const workflow = [
    {
      step: 1,
      title: 'Accedi alle Impostazioni',
      description: 'Clicca su "Impostazioni" nella sidebar'
    },
    {
      step: 2,
      title: 'Scegli la Sezione',
      description: 'Seleziona la sezione che vuoi configurare'
    },
    {
      step: 3,
      title: 'Modifica le Impostazioni',
      description: 'Cambia le impostazioni secondo le tue preferenze'
    },
    {
      step: 4,
      title: 'Salva le Modifiche',
      description: 'Le impostazioni vengono salvate automaticamente'
    },
    {
      step: 5,
      title: 'Testa le Funzioni',
      description: 'Verifica che tutto funzioni correttamente'
    }
  ];

  const themes = [
    {
      name: 'Chiaro',
      icon: <Sun className="h-6 w-6 text-yellow-500" />,
      description: 'Tema luminoso per ambienti ben illuminati',
      color: 'bg-yellow-100 text-yellow-800'
    },
    {
      name: 'Scuro',
      icon: <Moon className="h-6 w-6 text-blue-500" />,
      description: 'Tema scuro per ridurre l\'affaticamento degli occhi',
      color: 'bg-blue-100 text-blue-800'
    },
    {
      name: 'Auto',
      icon: <RefreshCw className="h-6 w-6 text-gray-500" />,
      description: 'Si adatta automaticamente alle impostazioni del sistema',
      color: 'bg-gray-100 text-gray-800'
    }
  ];

  const notificationTypes = [
    {
      type: 'Push',
      icon: <Bell className="h-5 w-5" />,
      description: 'Notifiche in tempo reale sul browser',
      color: 'bg-blue-100 text-blue-800'
    },
    {
      type: 'Email',
      icon: <Mail className="h-5 w-5" />,
      description: 'Email automatiche per reminder',
      color: 'bg-purple-100 text-purple-800'
    },
    {
      type: 'Reminder',
      icon: <CheckCircle className="h-5 w-5" />,
      description: 'Promemoria per colloqui programmati',
      color: 'bg-green-100 text-green-800'
    }
  ];

  const userRoles = [
    {
      role: 'Utente',
      description: 'Accesso base alle impostazioni',
      features: ['Aspetto', 'Notifiche', 'Account'],
      color: 'bg-blue-100 text-blue-800'
    },
    {
      role: 'Moderatore',
      description: 'Accesso esteso con funzioni di moderazione',
      features: ['Aspetto', 'Notifiche', 'Privacy', 'Account'],
      color: 'bg-purple-100 text-purple-800'
    },
    {
      role: 'Admin',
      description: 'Accesso completo a tutte le funzioni',
      features: ['Tutte le sezioni disponibili'],
      color: 'bg-red-100 text-red-800'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-2 lg:p-4 lg:pr-8 space-y-4 lg:space-y-6">
      <div className="w-full max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 lg:p-6">
          <div className="flex items-center gap-3 mb-4">
            <button
              onClick={() => navigate('/help')}
              className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl">
                <Settings className="h-6 w-6 text-gray-700" />
              </div>
              <div>
                <h1 className="text-xl lg:text-2xl font-bold text-gray-900">
                  Guida Impostazioni
                </h1>
                <p className="text-sm text-gray-600">
                  Scopri come personalizzare l\'applicazione secondo le tue preferenze
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Panoramica */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 lg:p-6">
          <div className="flex items-start gap-3 mb-4">
            <div className="p-2 bg-gray-100 rounded-lg">
              <Info className="h-5 w-5 text-gray-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">
                Panoramica
              </h2>
              <p className="text-gray-600 text-sm leading-relaxed">
                La pagina Impostazioni ti permette di personalizzare completamente l\'esperienza 
                dell\'applicazione. Qui puoi modificare l\'aspetto, gestire le notifiche, 
                configurare la privacy e molto altro. Le impostazioni disponibili variano 
                in base al tuo ruolo nel sistema (utente, moderatore, admin).
              </p>
            </div>
          </div>
        </div>

        {/* Funzionalità */}
        <div className="space-y-4 lg:space-y-6">
          {features.map((feature, index) => (
            <div key={feature.id} className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 lg:p-6">
              <div className="flex items-start gap-3 mb-4">
                <div className="p-2 bg-gray-100 rounded-lg">
                  {feature.icon}
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-3">
                    {feature.description}
                  </p>
                  <ul className="space-y-2">
                    {feature.details.map((detail, detailIndex) => (
                      <li key={detailIndex} className="flex items-start gap-2 text-sm text-gray-600">
                        <ChevronRight className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
                        <span dangerouslySetInnerHTML={{ __html: detail }} />
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Temi Disponibili */}
        <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-2xl border border-yellow-200 p-4 lg:p-6">
          <h3 className="text-lg font-semibold text-yellow-900 mb-4 flex items-center gap-2">
            <Palette className="h-5 w-5" />
            Temi Disponibili
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {themes.map((theme, index) => (
              <div key={index} className="bg-white rounded-xl p-4 border border-yellow-200">
                <div className="flex items-center gap-3 mb-2">
                  {theme.icon}
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${theme.color}`}>
                    {theme.name}
                  </span>
                </div>
                <p className="text-sm text-gray-700">{theme.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Tipi di Notifiche */}
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl border border-blue-200 p-4 lg:p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-4 flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Tipi di Notifiche
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {notificationTypes.map((type, index) => (
              <div key={index} className="bg-white rounded-xl p-4 border border-blue-200">
                <div className="flex items-center gap-3 mb-2">
                  {type.icon}
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${type.color}`}>
                    {type.type}
                  </span>
                </div>
                <p className="text-sm text-gray-700">{type.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Ruoli e Permessi */}
        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl border border-purple-200 p-4 lg:p-6">
          <h3 className="text-lg font-semibold text-purple-900 mb-4 flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Ruoli e Permessi
          </h3>
          <div className="space-y-4">
            {userRoles.map((role, index) => (
              <div key={index} className="bg-white rounded-xl p-4 border border-purple-200">
                <div className="flex items-center justify-between mb-2">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${role.color}`}>
                    {role.role}
                  </span>
                </div>
                <p className="text-sm text-gray-700 mb-2">{role.description}</p>
                <div className="text-xs text-gray-600">
                  <strong>Funzioni disponibili:</strong> {role.features.join(', ')}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Workflow */}
        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl border border-green-200 p-4 lg:p-6">
          <h3 className="text-lg font-semibold text-green-900 mb-4 flex items-center gap-2">
            <Target className="h-5 w-5" />
            Workflow Consigliato
          </h3>
          <div className="space-y-3">
            {workflow.map((step, index) => (
              <div key={index} className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                  {step.step}
                </div>
                <div>
                  <h4 className="font-semibold text-green-900 text-sm">{step.title}</h4>
                  <p className="text-green-800 text-sm">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Suggerimenti */}
        <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl border border-orange-200 p-4 lg:p-6">
          <h3 className="text-lg font-semibold text-orange-900 mb-4 flex items-center gap-2">
            <Info className="h-5 w-5" />
            Suggerimenti Utili
          </h3>
          <ul className="space-y-2">
            {tips.map((tip, index) => (
              <li key={index} className="flex items-start gap-2 text-sm text-orange-800">
                <div className="w-1.5 h-1.5 bg-orange-600 rounded-full mt-2 flex-shrink-0"></div>
                <span>{tip}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Azioni */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 lg:p-6">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Pronto a personalizzare l\'applicazione?
            </h3>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => navigate('/settings')}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
              >
                <Settings className="h-4 w-4" />
                Vai alle Impostazioni
              </button>
              <button
                onClick={() => navigate('/profile')}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                <User className="h-4 w-4" />
                Profilo Utente
              </button>
              <button
                onClick={() => navigate('/help')}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
              >
                <Info className="h-4 w-4" />
                Altre Guide
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 